import { Dispatch, SetStateAction, useState } from 'react';
import {
  BaseResponseData,
  CheckCodeData,
  FormNotifications,
  CurrentUser,
  VerifyPhoneNumberData,
  MappableObject,
  StepperStep,
  LocationData,
} from 'types';
import {
  sendAccountInfoStepData,
  sendAccountTypeStepData,
  sendActivityStepData,
  verifyCodeFromPhone,
  verifyPhoneNumber,
} from 'api';
import { getDataFromLocalStore, setDataToLocalStore } from 'utils/localStoreTools';
import {
  countDownNotification,
  formatErrorsForFormNotification,
  formatServerErrorsForNotification,
} from 'utils/services';
import { getResendCodeErrorNotification } from 'utils/formatters';
import { getActivitiesStep } from './activities';
import { getLocationStep } from './location';
import { getAccountInfoStep } from './accountInfo';
import { getInitialPhoneNumberStep, getCheckingPhoneNumberStep } from './phoneNumber';
import { getAccountTypeStep } from './accountType';

export type GetPlainSignUpStepsProps = {
  activities: Record<string, string>;
  goToNextStep: (newUser: CurrentUser, skipSteps?: number) => void;
  notifications: Record<string, FormNotifications>;
  onLocationPass: (data: LocationData) => void;
  setNotifications: Dispatch<SetStateAction<Record<string, FormNotifications>>>;
  stepBack: () => void;
  user: CurrentUser;
};

export const GetPlainSignUpSteps = (properties: GetPlainSignUpStepsProps): StepperStep[] => {
  const { goToNextStep, user, setNotifications, stepBack, notifications, onLocationPass, activities } =
    properties;
  const [isLoading, setIsLoading] = useState(false);
  const [verifyData, setVerifyData] = useState({});
  const callbackMap: MappableObject<Function> = {
    signUpStep1: sendAccountTypeStepData,
    signUpStep2: sendAccountInfoStepData,
    signUpStep4: sendActivityStepData,
  };

  const onSetPhonePass = async (data: VerifyPhoneNumberData, resend?: boolean) => {
    if (isLoading) return;
    setIsLoading(true);
    setVerifyData(data)
    const response = await verifyPhoneNumber(data);
    if (response.ok) {
      setDataToLocalStore('phone_code_sent', JSON.stringify(Date.now()));
      if(!resend)
        goToNextStep({ ...user, uncheckedPhone: data.phone }, response.status === 'not_need' ? 0 : 0);
      setNotifications((notifications: Record<string, FormNotifications>) => {
        notifications.signUpStep0_0 = {} as FormNotifications;
        delete notifications.signUpStep0_0;
        return notifications;
      });
    } else {
      if (response.seconds_left) {
        countDownNotification({
          fieldName: resend ? 'signUpStep0_1' : 'signUpStep0_0',
          callback: setNotifications,
          formatter: getResendCodeErrorNotification,
          message: response.message || '',
          timer: response.seconds_left || 0,
        });
      } else {
        setNotifications((notifications) => ({
          ...notifications,
          ...formatErrorsForFormNotification(response, 'signUpStep0_0')
        }));
      }
    }
    setIsLoading(false);
  };

  const onCheckCodePass = async (data: CheckCodeData) => {
    const codeTimestamp = getDataFromLocalStore('phone_code_sent');
    const codeLifetime = 3600000;
    if (!codeTimestamp || codeTimestamp + codeLifetime < Date.now()) {
      setNotifications((notifications) => ({
        ...notifications,
        signUpStep0_0: {
          mode: 'error',
          header: 'Your code expired',
          message: 'Please, asc for new one',
        },
      }));
      stepBack();
    }

    if (isLoading) return;
    setIsLoading(true);
    const response: BaseResponseData<CurrentUser> = await verifyCodeFromPhone(data);
    if (response.ok && response.data) {
      goToNextStep(response.data);
      setNotifications((notifications) => {
        notifications.signUpStep0_1 = {} as FormNotifications;
        delete notifications.signUpStep0_1;
        return notifications;
      });
    } else {
      setNotifications((notifications) => ({
        ...notifications,
        signUpStep0_1: {
          mode: 'error',
          header: 'Error while sending code',
          message: response.message,
        },
      }));
    }
    setIsLoading(false);
  };

  const onPass = async (data: any, id: string) => {
    if (isLoading) return;
    setIsLoading(true);
    const response: BaseResponseData<CurrentUser> = await callbackMap[id]?.(data);

    console.log(response?.data)
    if (response?.ok && response.data) {
      goToNextStep(response.data, (id==='signUpStep2' && (response.data?.registration_step!=='location')) ? 1 : 0 );
      setNotifications((notifications) => {
        notifications[id] = {} as FormNotifications;
        delete notifications[id];
        return notifications;
      });
    } else if (response) {
      setNotifications((notifications) => ({
        ...notifications,
        [id]: formatServerErrorsForNotification(response),
      }));
    }
    setIsLoading(false);
  };

  return [
    getInitialPhoneNumberStep(user, notifications, onSetPhonePass),
    getCheckingPhoneNumberStep(user, notifications, stepBack, () => onSetPhonePass(verifyData, true), onCheckCodePass),
    getAccountTypeStep(user, notifications, onPass),
    getAccountInfoStep(user, notifications, onPass, setNotifications),
    getLocationStep(user, notifications, onLocationPass),
    getActivitiesStep(user, notifications, activities, onPass),
  ];
};
